// components/Shared/Header.js

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { getUserDetails } from '../../services/authService';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const { logout } = useContext(AuthContext);

  useEffect(() => {
    const fetchUserData = () => {
      getUserDetails()
        .then((response) => {
          setUserEmail(response.data.email);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          if (error.response && error.response.status === 401) {
            // Handle token refresh here if implemented
            logout();

          } else {
            console.error(error);
            logout();
          }
        });
    };

    fetchUserData();
  }, [logout]);

  const handleClick = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <header className='shadow-md font-[sans-serif] tracking-wide relative z-50'>
      {/* First Header */}
      <section className='flex items-center justify-between py-3 lg:px-10 px-4 border-gray-200 border-b bg-white'>
        {/* Left Spacer */}

        {/* Center: Logo */}
        <div className='flex-1 flex justify-center'>
          <a href='/'>
            <img
              src='https://readymadeui.com/readymadeui.svg'
              alt='logo'
              className='w-[160px]'
            />
          </a>
        </div>

        {/* Right Side: Login/Dashboard Button and Mobile Menu Toggle */}
        <div className='flex items-center space-x-4'>
          {loading ? (
            <div></div>
          ) : userEmail ? (
            <div className='relative hidden lg:block'>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className='px-4 py-2 text-sm rounded font-semibold text-[#333] border-2 border-[#333] bg-transparent flex items-center'
              >
                Menu
                <svg
                  className="ml-2 w-4 h-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 12l-6-6h12l-6 6z" />
                </svg>
              </button>

              {dropdownOpen && (
                <div className='absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg'>
                  <a
                    href='/dashboard'
                    className='block px-4 py-2 text-gray-800 hover:bg-gray-100'
                  >
                    Dashboard
                  </a>
                  <a
                    href='/profile'
                    className='block px-4 py-2 text-gray-800 hover:bg-gray-100'
                  >
                    Profile
                  </a>
                  <a
                    href='/edit-profile'
                    className='block px-4 py-2 text-gray-800 hover:bg-gray-100'
                  >
                    Edit Profile
                  </a>
                  <button
                    onClick={logout}
                    className='w-full text-left block px-4 py-2 text-gray-800 hover:bg-gray-100'
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className='px-4 py-2 text-sm rounded font-semibold text-[#333] border-2 border-[#333] bg-transparent'>
              <a href='/login'>Sign In</a>
            </button>
          )}
          {/* Mobile Menu Toggle */}
          <div className='lg:hidden'>
            <button onClick={handleClick}>
              <svg
                className='w-7 h-7'
                fill='#333'
                viewBox='0 0 20 20'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  fillRule='evenodd'
                  d='M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z'
                  clipRule='evenodd'
                ></path>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Second Header */}
      <nav className='bg-[#333] hidden lg:block'>
        <div className='container mx-auto flex items-center justify-center px-4 py-2'>
          {/* Navigation Links */}
          <ul className='flex space-x-6'>
            <li>
              <a
                href='/'
                className='text-white hover:text-yellow-300 text-[15px] font-medium'
              >
                Home
              </a>
            </li>
            <li>
              <a
                href='#'
                className='text-white hover:text-yellow-300 text-[15px] font-medium'
              >
                About Us
              </a>
            </li>
            <li>
              <a
                href='#'
                className='text-white hover:text-yellow-300 text-[15px] font-medium'
              >
                Products
              </a>
            </li>
            <li>
              <a
                href='#'
                className='text-white hover:text-yellow-300 text-[15px] font-medium'
              >
                Services
              </a>
            </li>
            <li>
              <a
                href='#'
                className='text-white hover:text-yellow-300 text-[15px] font-medium'
              >
                Contact Us
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          className='lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50'
          onClick={handleClick}
        >
          <div
            className='fixed top-0 left-0 w-3/4 max-w-xs bg-white h-full shadow-md z-50 p-4 overflow-auto'
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className='absolute top-2 right-2 text-gray-600'
              onClick={handleClick}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='w-6 h-6'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
            {/* Mobile Menu Content */}
            <div className='mb-6'>
              <a href='/'>
                <img
                  src='https://readymadeui.com/readymadeui.svg'
                  alt='logo'
                  className='w-36'
                />
              </a>
            </div>
            {/* Navigation Links */}
            <ul className='space-y-4'>
              <li>
                <a
                  href='/'
                  className='hover:text-yellow-300 text-gray-800 text-[15px] font-medium block'
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href='#'
                  className='hover:text-yellow-300 text-gray-800 text-[15px] font-medium block'
                >
                  About Us
                </a>
              </li>
              <li>
                <a
                  href='#'
                  className='hover:text-yellow-300 text-gray-800 text-[15px] font-medium block'
                >
                  Products
                </a>
              </li>
              <li>
                <a
                  href='#'
                  className='hover:text-yellow-300 text-gray-800 text-[15px] font-medium block'
                >
                  Services
                </a>
              </li>
              <li>
                <a
                  href='#'
                  className='hover:text-yellow-300 text-gray-800 text-[15px] font-medium block'
                >
                  Contact Us
                </a>
              </li>
            </ul>
            {/* Login/Dashboard Button */}
            <div className='mt-6'>
              {loading ? (
                <div></div>
              ) : userEmail ? (
                <div>
                  <p className='text-gray-800 text-[15px] font-medium mb-2'>
                    {userEmail}
                  </p>
                  <a
                    href='/dashboard'
                    className='block w-full px-4 py-2 text-sm rounded font-semibold text-[#333] border-2 border-[#333] bg-transparent text-center mb-2'
                  >
                    Dashboard
                  </a>
                  <a
                    href='/profile'
                    className='block w-full px-4 py-2 text-sm rounded font-semibold text-[#333] border-2 border-[#333] bg-transparent text-center'>
                    Profile
                  </a>
                  <a
                    href='/edit-profile'
                    className='block w-full px-4 py-2 text-sm rounded font-semibold text-[#333] border-2 border-[#333] bg-transparent text-center'>
                    Edit Profile
                  </a>
                  <button
                    onClick={logout}
                    className='block w-full px-4 py-2 text-sm rounded font-semibold text-[#333] border-2 border-[#333] bg-transparent text-center'
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <button className='w-full px-4 py-2 text-sm rounded font-semibold text-[#333] border-2 border-[#333] bg-transparent'>
                  <a href='/login'>Sign In</a>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
